import fs from 'fs-extra';
import path from 'path';
import { dump, load } from 'js-yaml';
import { FileSystemAdapter } from './file-system-adapter.js';
import { ConfigValidator } from './config-validator.js';

/**
 * Core LLMail configuration interface.
 * All paths are relative to the config file location.
 */
export interface LlmailConfig {
  types_list: string[];
  active_states: string[];
  inactive_reasons: string[];
  issues_dir: string;
  inbox_dir: string;
  type_dirs?: string[];
  plugins?: string | string[] | Array<{
    name: string;
    config?: Record<string, any>;
  }>;
}

export type ResolvedConfig = LlmailConfig;

const defaultConfig: LlmailConfig = {
  types_list: [],
  active_states: [],
  inactive_reasons: [],
  issues_dir: 'issues',
  inbox_dir: 'inbox'  // Changed to be relative to root
};

/**
 * Normalize paths in config.
 * All paths in the config should remain relative to the config file location.
 */
function normalizeConfigPaths(config: LlmailConfig): LlmailConfig {
  // Normalize paths but keep them relative
  return {
    ...config,
    issues_dir: path.normalize(config.issues_dir),
    inbox_dir: path.normalize(config.inbox_dir)
  };
}

/**
 * Load and validate config from a file
 */
export async function loadConfigFromFile(fs: FileSystemAdapter, configPath: string, rootDir: string): Promise<LlmailConfig> {
  const exists = await fs.exists(configPath);
  
  if (!exists) {
    // Return default config with normalized paths
    return normalizeConfigPaths(defaultConfig);
  }

  try {
    const content = await fs.readFile(configPath);
    let config: any;
    
    try {
      config = load(content) as LlmailConfig;
    } catch (err) {
      throw new Error('Failed to load config');
    }

    // Merge with defaults, ensuring default types are preserved
    const mergedConfig = {
      ...defaultConfig,
      ...config,
      // Don't merge arrays - use config values if present, otherwise use defaults
      types_list: config.types_list || defaultConfig.types_list,
      active_states: config.active_states || defaultConfig.active_states,
      inactive_reasons: config.inactive_reasons || defaultConfig.inactive_reasons
    };

    // Validate config
    const validator = new ConfigValidator();
    const result = validator.validateConfig(mergedConfig);
    if (!result.valid) {
      throw new Error(`Invalid config: ${result.errors[0]}`);
    }

    // Normalize paths but keep them relative
    return normalizeConfigPaths(mergedConfig);
  } catch (err) {
    if (err instanceof Error) {
      throw err;
    }
    throw new Error('Failed to load config');
  }
}

/**
 * Load llmail config from the default location or specified path
 */
export async function loadLlmailConfig(fs: FileSystemAdapter, rootDir: string = process.cwd(), configPath?: string): Promise<LlmailConfig> {
  const resolvedConfigPath = configPath || path.join(rootDir, 'llmail.yaml');
  try {
    return await loadConfigFromFile(fs, resolvedConfigPath, rootDir);
  } catch (err) {
    // If config file doesn't exist or can't be loaded, return defaults with normalized paths
    return normalizeConfigPaths(defaultConfig);
  }
}

/**
 * Save config to the default location
 */
export async function saveLlmailConfig(fs: FileSystemAdapter, config: LlmailConfig, configPath: string): Promise<void> {
  const yamlContent = dump(normalizeConfigPaths(config));
  
  // Ensure the directory exists
  const configDir = path.dirname(configPath);
  await fs.ensureDir(configDir);
  
  await fs.writeFile(configPath, yamlContent);
} 